import React, { useEffect, useRef, useState } from "react";
import axios from "../../api/axios";
import { uploadCustomAssetRoute, filesRoute } from "../../api/routes";
import DebugLogger from "../../helpers/DebugLogger";
import { convertUniqueFilenameToOriginal, getCampaignImages, getCampaignAudio, getCampaignVideos } from "../v2/CustomisationWorkspace/v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions";
import { IconJsxer } from "../../helpers/IconHelper";
import WaveSurfer from "wavesurfer.js";

const ACCEPT_ALL = ".png,.jpg,.jpeg,.gif,.bmp,.webp,.svg,.mp3,.wav,.ogg,.aac,.flac,.m4a,.mp4,.mov,.avi,.webm,.mkv";
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
const AUDIO_EXTS = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'];
const VIDEO_EXTS = ['mp4', 'mov', 'avi', 'webm', 'mkv'];

const TABS = [
    { id: 'images', label: 'Images' },
    { id: 'audio',  label: 'Audio'  },
    { id: 'videos', label: 'Videos' },
];

const MEDIA_FEATURE_CONFIG = {
    images: 'imageOverlay_simple',
    audio: 'disruptorAudio',
    videos: 'disruptorVideo',
};

const getUploadDirectory = (fileType) => {
    if (fileType === 'images') {
        return 'image';
    }

    return fileType;
};

const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (IMAGE_EXTS.includes(ext)) return 'images';
    if (AUDIO_EXTS.includes(ext)) return 'audio';
    if (VIDEO_EXTS.includes(ext)) return 'videos';
    return null;
};

const AudioPreview = ({ src, index }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const waveformId = `media-panel-waveform-${index}`;
    const [wavesurferInstance, setWavesurferInstance] = useState(null);

    useEffect(() => {
        if (src) {
            const ws = WaveSurfer.create({
                container: `#${waveformId}`,
                waveColor: '#BCA4FF',
                progressColor: '#9146FF',
                url: src,
                height: 18,
                normalize: true,
                hideScrollbar: true,
            });
            setWavesurferInstance(ws);
            return () => {
                ws.destroy();
                setWavesurferInstance(null);
            };
        }
    }, [src]);

    useEffect(() => {
        if (wavesurferInstance) {
            const onPlay = () => setIsPlaying(true);
            const onStop = () => setIsPlaying(false);
            wavesurferInstance.on('play', onPlay);
            wavesurferInstance.on('finish', onStop);
            wavesurferInstance.on('pause', onStop);
        }
        return () => {
            if (wavesurferInstance) {
                wavesurferInstance.un('play');
                wavesurferInstance.un('finish');
                wavesurferInstance.un('pause');
                wavesurferInstance.stop();
                wavesurferInstance.destroy();
            }
            setIsPlaying(false);
        };
    }, [wavesurferInstance]);

    if (!src) return null;

    return (
        <div className="media-audio-preview" onClick={(e) => e.stopPropagation()}>
            <div
                className={`play-button-square${wavesurferInstance ? '' : ' btn-off'}`}
                onClick={wavesurferInstance ? () => { wavesurferInstance.isPlaying() ? wavesurferInstance.stop() : wavesurferInstance.play(); } : null}
            >
                {IconJsxer.GetIcon(isPlaying ? IconJsxer.ICONS.stop : IconJsxer.ICONS.play, IconJsxer.ICON_STYLES.campaignPanelInfoPill)}
            </div>
            <div id={waveformId} className="audio-waveform-preview-holder" />
        </div>
    );
};

const MediaPanel = ({ campaignData, onDragStart, onDragEnd }) => {
    const [currentTab, setCurrentTab] = useState('images');
    const [busy, setBusy] = useState(false);
    const [isDraggingAsset, setIsDraggingAsset] = useState(false);
    const uploadInputRef = useRef(null);
    const tabsRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const [campaignImages, setCampaignImages] = useState([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    const [campaignAudio, setCampaignAudio] = useState([]);
    const [loadingAudio, setLoadingAudio] = useState(false);
    const [audioLoaded, setAudioLoaded] = useState(false);

    const [campaignVideos, setCampaignVideos] = useState([]);
    const [loadingVideos, setLoadingVideos] = useState(false);
    const [videosLoaded, setVideosLoaded] = useState(false);
    const videoThumbRefs = useRef({});
    const [videoThumbPlayState, setVideoThumbPlayState] = useState({});
    const dragPreviewRef = useRef(null);

    const campaignId = campaignData?._id;

    const updateScrollButtons = () => {
        if (tabsRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    useEffect(() => {
        updateScrollButtons();
        const el = tabsRef.current;
        if (el) {
            el.addEventListener('scroll', updateScrollButtons);
            window.addEventListener('resize', updateScrollButtons);
            return () => {
                el.removeEventListener('scroll', updateScrollButtons);
                window.removeEventListener('resize', updateScrollButtons);
            };
        }
    }, []);

    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setPreviewImage(null);
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, []);

    const scrollTabs = (direction) => {
        if (tabsRef.current) {
            tabsRef.current.scrollLeft += direction === 'right' ? 80 : -80;
        }
    };

    useEffect(() => {
        if (currentTab === 'images' && campaignId && !imagesLoaded && !loadingImages) {
            setLoadingImages(true);
            getCampaignImages(campaignId)
                .then(images => { setCampaignImages(images); setImagesLoaded(true); })
                .catch(err => DebugLogger.error('MediaPanel: Failed to load images:', err))
                .finally(() => setLoadingImages(false));
        }
    }, [currentTab, campaignId, imagesLoaded, loadingImages]);

    useEffect(() => {
        if (currentTab === 'audio' && campaignId && !audioLoaded && !loadingAudio) {
            setLoadingAudio(true);
            getCampaignAudio(campaignId)
                .then(audio => { setCampaignAudio(audio); setAudioLoaded(true); })
                .catch(err => DebugLogger.error('MediaPanel: Failed to load audio:', err))
                .finally(() => setLoadingAudio(false));
        }
    }, [currentTab, campaignId, audioLoaded, loadingAudio]);

    useEffect(() => {
        if (currentTab === 'videos' && campaignId && !videosLoaded && !loadingVideos) {
            setLoadingVideos(true);
            getCampaignVideos(campaignId)
                .then(videos => { setCampaignVideos(videos); setVideosLoaded(true); })
                .catch(err => DebugLogger.error('MediaPanel: Failed to load videos:', err))
                .finally(() => setLoadingVideos(false));
        }
    }, [currentTab, campaignId, videosLoaded, loadingVideos]);

    const handleUploadClick = () => {
        if (!busy) uploadInputRef.current.click();
    };

    const buildDraggedMediaItem = (asset, mediaType) => {
        const resolvedFilename = asset.filename || asset.name || 'Media Asset';
        const resolvedName = convertUniqueFilenameToOriginal(resolvedFilename);
        return {
            id: `media-${mediaType}-${resolvedFilename}`,
            label: resolvedName,
            varKey: MEDIA_FEATURE_CONFIG[mediaType],
            isMediaAsset: true,
            mediaType,
            asset: {
                ...asset,
                filename: resolvedFilename,
                type: mediaType,
                displayName: resolvedName,
            },
            iconCategory: 'core',
        };
    };

    const handleAssetDragStart = (asset, mediaType, event) => {
        setIsDraggingAsset(true);
        event.currentTarget?.classList?.add('media-item-card--dragging');

        if (mediaType === 'images' && event.dataTransfer && asset?.url) {
            if (dragPreviewRef.current) {
                dragPreviewRef.current.remove();
                dragPreviewRef.current = null;
            }

            const dragPreviewNode = document.createElement('div');
            dragPreviewNode.style.position = 'fixed';
            dragPreviewNode.style.top = '-10000px';
            dragPreviewNode.style.left = '-10000px';
            dragPreviewNode.style.width = '144px';
            dragPreviewNode.style.height = '144px';
            dragPreviewNode.style.borderRadius = '12px';
            dragPreviewNode.style.border = '1px solid rgba(148, 163, 184, 0.55)';
            dragPreviewNode.style.background = '#0f172a';
            dragPreviewNode.style.boxShadow = '0 10px 24px rgba(2, 6, 23, 0.34)';
            dragPreviewNode.style.overflow = 'hidden';
            dragPreviewNode.style.pointerEvents = 'none';
            dragPreviewNode.style.margin = '0';
            dragPreviewNode.style.zIndex = '-1';

            const previewImage = document.createElement('img');
            previewImage.src = asset.url;
            previewImage.alt = '';
            previewImage.style.width = '100%';
            previewImage.style.height = '100%';
            previewImage.style.objectFit = 'cover';
            previewImage.draggable = false;
            dragPreviewNode.appendChild(previewImage);

            document.body.appendChild(dragPreviewNode);
            dragPreviewRef.current = dragPreviewNode;
            event.dataTransfer.setDragImage(dragPreviewNode, 72, 72);
        }

        if (!onDragStart) {
            return;
        }

        onDragStart(buildDraggedMediaItem(asset, mediaType), event);
    };

    const handleAssetDragEnd = (event) => {
        event.currentTarget?.classList?.remove('media-item-card--dragging');

        if (dragPreviewRef.current) {
            dragPreviewRef.current.remove();
            dragPreviewRef.current = null;
        }

        setIsDraggingAsset(false);
        onDragEnd && onDragEnd(event);
    };

    const handleVideoThumbLoadedMetadata = (event) => {
        const videoElement = event.currentTarget;
        if (!videoElement || videoElement.dataset.midpointThumbApplied === 'true') {
            return;
        }

        const duration = Number(videoElement.duration);
        if (!Number.isFinite(duration) || duration <= 0) {
            return;
        }

        // Use midpoint frame so transition clips that start transparent don't show as black tiles.
        const midpoint = Math.min(Math.max(duration / 2, 0.1), Math.max(duration - 0.1, 0.1));
        videoElement.dataset.midpointThumbApplied = 'true';
        videoElement.dataset.thumbTime = String(midpoint);

        const seekToMidpoint = () => {
            videoElement.currentTime = midpoint;
            videoElement.removeEventListener('canplay', seekToMidpoint);
        };

        if (videoElement.readyState >= 1) {
            seekToMidpoint();
        } else {
            videoElement.addEventListener('canplay', seekToMidpoint, { once: true });
        }
    };

    const setVideoThumbRef = (videoKey, element) => {
        if (!element) {
            delete videoThumbRefs.current[videoKey];
            return;
        }

        videoThumbRefs.current[videoKey] = element;
    };

    const toggleVideoThumbPlayback = (videoKey) => {
        const currentVideo = videoThumbRefs.current[videoKey];
        if (!currentVideo) {
            return;
        }

        const shouldPlay = currentVideo.paused || currentVideo.ended;

        Object.entries(videoThumbRefs.current).forEach(([key, element]) => {
            if (key !== videoKey && element && !element.paused) {
                element.pause();
                const thumbTime = Number(element.dataset.thumbTime);
                if (Number.isFinite(thumbTime)) {
                    element.currentTime = thumbTime;
                }
            }
        });

        if (shouldPlay) {
            currentVideo.currentTime = 0;
            currentVideo.play().catch(() => {
                // Ignore play failures (browser policy/state race) for thumbnail previews.
            });
        } else {
            currentVideo.pause();
            const thumbTime = Number(currentVideo.dataset.thumbTime);
            if (Number.isFinite(thumbTime)) {
                currentVideo.currentTime = thumbTime;
            }
        }
    };

    const setVideoPlayingState = (videoKey, isPlaying) => {
        setVideoThumbPlayState((prevState) => {
            if (prevState[videoKey] === isPlaying) {
                return prevState;
            }

            return {
                ...prevState,
                [videoKey]: isPlaying,
            };
        });
    };

    const handleFileSelected = async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        e.target.value = '';

        const fileType = getFileType(file.name);
        if (!fileType) {
            alert('Unsupported file type.');
            return;
        }

        setBusy(true);
        try {
            const uploadDirectory = getUploadDirectory(fileType);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('filename', file.name);
            formData.append('type', uploadDirectory);
            if (campaignId) formData.append('campaignID', campaignId);

            const response = await axios.post(uploadCustomAssetRoute, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const uploadedFilename = response.data.uploadedFilename;
            const uploadType = response.data.type;

            let assetUrl;
            if (uploadType === 'local') {
                const campaignPath = campaignId ? `campaigns/${campaignId}/` : '';
                assetUrl = `${filesRoute}/feature-assets/${campaignPath}${uploadDirectory}/${uploadedFilename}`;
            } else {
                assetUrl = uploadedFilename;
            }

            const newAsset = { filename: uploadedFilename, name: uploadedFilename, url: assetUrl, type: fileType };

            if (fileType === 'images') {
                setCampaignImages(prev => [...prev, newAsset]);
                setImagesLoaded(true);
            } else if (fileType === 'audio') {
                setCampaignAudio(prev => [...prev, newAsset]);
                setAudioLoaded(true);
            } else if (fileType === 'videos') {
                setCampaignVideos(prev => [...prev, newAsset]);
                setVideosLoaded(true);
            }
            setCurrentTab(fileType);
        } catch (error) {
            DebugLogger.error('MediaPanel: Upload failed:', error);
            alert('Failed to upload file. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    const renderImagesTab = () => {
        if (loadingImages) return <div className="media-status-message">Loading images...</div>;
        if (campaignImages.length === 0) return <div className="media-status-message">No images uploaded yet. Use the button above to upload.</div>;
        return (
            <div className="media-items-grid">
                {campaignImages.map((image, index) => (
                    <div
                        key={index}
                        className="media-item-card image-card"
                        title={convertUniqueFilenameToOriginal(image.name)}
                        draggable={true}
                        onDragStart={(event) => handleAssetDragStart(image, 'images', event)}
                        onDragEnd={handleAssetDragEnd}
                        onClick={() => {
                            setPreviewImage({
                                name: convertUniqueFilenameToOriginal(image.name),
                                url: image.url,
                            });
                        }}
                    >
                        <div className="media-item-thumb image-thumb">
                            <img
                                src={image.url}
                                alt={convertUniqueFilenameToOriginal(image.name)}
                                draggable={false}
                                onDragStart={(event) => event.preventDefault()}
                            />
                            <div className="image-thumb-magnify-overlay">
                                <span className="image-thumb-magnify-icon">⌕</span>
                            </div>
                        </div>
                        <div className="media-item-name">{convertUniqueFilenameToOriginal(image.name)}</div>
                    </div>
                ))}
            </div>
        );
    };

    const renderAudioTab = () => {
        if (loadingAudio) return <div className="media-status-message">Loading audio...</div>;
        if (campaignAudio.length === 0) return <div className="media-status-message">No audio files uploaded yet. Use the button above to upload.</div>;
        return (
            <div className="media-items-grid media-items-grid--audio">
                {campaignAudio.map((audio, index) => (
                    <div
                        key={index}
                        className="media-item-card audio-card"
                        title={convertUniqueFilenameToOriginal(audio.name)}
                        draggable={true}
                        onDragStart={(event) => handleAssetDragStart(audio, 'audio', event)}
                        onDragEnd={handleAssetDragEnd}
                    >
                        <div className="media-item-name">{convertUniqueFilenameToOriginal(audio.name)}</div>
                        <AudioPreview src={audio.url} index={index} />
                    </div>
                ))}
            </div>
        );
    };

    const renderVideosTab = () => {
        if (loadingVideos) return <div className="media-status-message">Loading videos...</div>;
        if (campaignVideos.length === 0) return <div className="media-status-message">No videos uploaded yet. Use the button above to upload.</div>;
        return (
            <div className="media-items-grid media-items-grid--videos">
                {campaignVideos.map((video, index) => (
                    (() => {
                        const videoKey = video.filename || video.name || `video-${index}`;
                        const isPlaying = Boolean(videoThumbPlayState[videoKey]);

                        return (
                    <div
                        key={index}
                        className="media-item-card video-card"
                        title={convertUniqueFilenameToOriginal(video.name)}
                        draggable={true}
                        onDragStart={(event) => handleAssetDragStart(video, 'videos', event)}
                        onDragEnd={handleAssetDragEnd}
                    >
                        <div className="media-item-thumb video-thumb">
                            <video
                                ref={(element) => setVideoThumbRef(videoKey, element)}
                                draggable={false}
                                preload="metadata"
                                muted
                                playsInline
                                onLoadedMetadata={handleVideoThumbLoadedMetadata}
                                onPlay={() => setVideoPlayingState(videoKey, true)}
                                onPause={() => setVideoPlayingState(videoKey, false)}
                                onEnded={(event) => {
                                    setVideoPlayingState(videoKey, false);
                                    const thumbTime = Number(event.currentTarget.dataset.thumbTime);
                                    if (Number.isFinite(thumbTime)) {
                                        event.currentTarget.currentTime = thumbTime;
                                    }
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    toggleVideoThumbPlayback(videoKey);
                                }}
                                onDragStart={(event) => event.preventDefault()}
                            >
                                <source src={video.url} />
                            </video>
                            <div
                                className="video-thumb-play-overlay"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    toggleVideoThumbPlayback(videoKey);
                                }}
                            >
                                <span className="video-thumb-play-icon">
                                    {isPlaying ? '■' : '▶'}
                                </span>
                            </div>
                        </div>
                        <div className="media-item-name">{convertUniqueFilenameToOriginal(video.name)}</div>
                    </div>
                        );
                    })()
                ))}
            </div>
        );
    };

    return (
        <div className={`media-panel${isDraggingAsset ? ' media-panel--dragging' : ''}`}>
            {/* Compact upload button */}
            <div className="media-panel-upload-row">
                <button
                    className={`media-upload-btn${busy ? ' busy' : ''}`}
                    onClick={handleUploadClick}
                    disabled={busy}
                >
                    <span className="upload-icon">&#8679;</span>
                    {busy ? 'Uploading...' : 'Upload Media Asset'}
                </button>
                <input
                    type="file"
                    accept={ACCEPT_ALL}
                    onChange={handleFileSelected}
                    hidden
                    ref={uploadInputRef}
                />
            </div>

            {/* Lozenge tabs with scroll arrows — same pattern as LibraryPanel */}
            <div className="library-categories-wrapper media-type-tabs">
                <button
                    className="category-scroll-btn"
                    onClick={() => scrollTabs('left')}
                    disabled={!canScrollLeft}
                    title="Scroll left"
                >
                    &#8249;
                </button>
                <div className="library-categories" ref={tabsRef}>
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            className={`category-tab${currentTab === tab.id ? ' active' : ''}`}
                            onClick={() => setCurrentTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <button
                    className="category-scroll-btn"
                    onClick={() => scrollTabs('right')}
                    disabled={!canScrollRight}
                    title="Scroll right"
                >
                    &#8250;
                </button>
            </div>

            {/* Scrollable content area */}
            <div className="media-panel-items" onWheel={(e) => e.stopPropagation()}>
                {currentTab === 'images' && renderImagesTab()}
                {currentTab === 'audio'  && renderAudioTab()}
                {currentTab === 'videos' && renderVideosTab()}
            </div>

            {previewImage && (
                <div className="flow-modal-overlay" onClick={() => setPreviewImage(null)}>
                    <div className="flow-modal media-image-preview-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="media-image-preview-header">
                            <h3>{previewImage.name}</h3>
                            <button
                                type="button"
                                className="media-image-preview-close"
                                onClick={() => setPreviewImage(null)}
                                aria-label="Close image preview"
                            >
                                ×
                            </button>
                        </div>
                        <div className="media-image-preview-body">
                            <img src={previewImage.url} alt={previewImage.name} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MediaPanel;
